import { useTranslation } from 'react-i18next';

import type { GetAssistantSessionsBySessionIdMessages200MessagesItem as ChatMessage } from '@/api/generated/lucioAPI.schemas';
import copyIcon from '@/assets/icons/copyIcon.svg';
import { Button } from '@/components/ui/button';
import type { EmailDraft } from '@/utils/emailContentDisplayUtils';
import { useCopyToClipboard } from '@/utils/hooks/useCopyToClipboard';

import { CitationAwareMarkdown } from './CitationAwareMarkdown';
import EmailDraftCard from './EmailDraftCard';
import RevisionDraftCard from './RevisionDraftCard';

function tryParseDraft(content: string | null | undefined): EmailDraft | null {
  if (!content) {
    return null;
  }
  try {
    const parsed = JSON.parse(content);
    if (parsed && typeof parsed === 'object' && 'subject' in parsed && 'body' in parsed) {
      return {
        body: String(parsed.body ?? ''),
        subject: String(parsed.subject ?? ''),
        to: String(parsed.to ?? ''),
        ...(parsed.cc && { cc: String(parsed.cc) }),
        ...(parsed.bcc && { bcc: String(parsed.bcc) }),
      };
    }
    return null;
  } catch {
    return null;
  }
}

const MessageBubble = ({
  isGenerating = false,
  message,
  onViewMail,
}: {
  isGenerating?: boolean;
  message: ChatMessage;
  onViewMail?: ((messageId: string) => void) | undefined;
}) => {
  const { t } = useTranslation();
  const { copyToClipboard } = useCopyToClipboard({
    successMessage: t('assistant.message.copied'),
  });

  const handleCopy = () => {
    copyToClipboard(message.content);
  };

  const isTextMessage =
    message.role === 'ASSISTANT' && (!message.contentType || message.contentType === 'text');

  return (
    <div className={`flex ${message.role === 'USER' ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`rounded-xl border-[0.42px] px-3 py-2 ${
          message.role === 'USER'
            ? 'bg-back-bg text-foreground border-border-subtle max-w-[80%] font-medium'
            : 'text-primary max-w-[94%] border-transparent bg-transparent'
        } ${message?.contentType === 'revision' ? 'w-full' : ''} ${isTextMessage ? 'relative' : ''}`}
      >
        {
          <div
            className={`prose prose-sm max-w-none text-xs leading-[170%] wrap-break-word [&_li]:ml-0 [&_li>p]:m-0 [&_li>p]:inline [&_ol]:list-decimal [&_ol]:pl-4 [&_p]:mt-0 [&_ul]:list-disc [&_ul]:pl-4 ${isTextMessage ? 'normalize-fonts' : ''}`}
          >
            {message?.contentType === 'email_draft' ? (
              (() => {
                const draft = tryParseDraft(message.content);
                return draft ? <EmailDraftCard draft={draft} isGenerating={isGenerating} /> : null;
              })()
            ) : message?.contentType === 'revision' ? (
              (() => {
                const revised = tryParseDraft(message.content);
                const original = tryParseDraft(message.originalContent);
                return revised && original ? (
                  <RevisionDraftCard
                    isGenerating={isGenerating}
                    messageId={message.id}
                    original={original}
                    revised={revised}
                    sessionId={message.sessionV2Id}
                  />
                ) : null;
              })()
            ) : (
              <CitationAwareMarkdown
                assistantChat={message.role === 'ASSISTANT'}
                citations={message.role === 'ASSISTANT' ? message.citations : undefined}
                content={message.content}
                isGenerating={isGenerating}
                onViewMail={onViewMail}
              />
            )}
          </div>
        }
        {isTextMessage && !isGenerating && message?.content && (
          <div className="mt-2 flex">
            <Button
              className="hover:bg-elevated-subtle flex h-6 w-6 items-center justify-center rounded-full bg-transparent p-0 shadow-none"
              onClick={handleCopy}
            >
              <img
                alt={t('assistant.message.copyAlt')}
                className="icon-themed h-4 w-4"
                src={copyIcon}
              />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
